import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  AfterViewInit
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { RouterModule } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, BehaviorSubject, combineLatest } from 'rxjs';
import { take, takeUntil, map } from 'rxjs/operators';
import { TipoContrato } from '../../models/tipo-contrato.interface';
import { TIPO_CONTRATO_LABELS } from './tipo-contrato.labels';
import * as TipoContratoActions from '../../store/tipo-contrato.actions';
import * as TipoContratoSelectors from '../../store/tipo-contrato.selectors';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { LoggerService } from '../../../../core/services/logger.service';

@Component({
  selector: 'app-tipo-contrato',
  templateUrl: './tipo-contrato.component.html',
  styleUrls: ['./tipo-contrato.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatPaginatorModule,
    RouterModule
  ]
})
export class TipoContratoComponent implements OnInit, OnDestroy, AfterViewInit {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly formBuilder = inject(FormBuilder);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly notificationService = inject(NotificationService);
  private readonly logger = inject(LoggerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly labels = TIPO_CONTRATO_LABELS;

  // Observables del store (same as actuacion-contractual)
  tiposContrato$ = this.store.select(TipoContratoSelectors.selectAllTiposContrato);
  loading$ = this.store.select(TipoContratoSelectors.selectTipoContratoLoading);
  error$ = this.store.select(TipoContratoSelectors.selectTipoContratoError);
  creating$ = this.store.select(TipoContratoSelectors.selectTipoContratoCreating);
  updating$ = this.store.select(TipoContratoSelectors.selectTipoContratoUpdating);
  deleting$ = this.store.select(TipoContratoSelectors.selectTipoContratoDeleting);

  // Filtros por columnas
  columnFilters: { nombre: string } = {
    nombre: ''
  };

  // Subjects de filtrado reactivo (same as actuacion-contractual)
  private readonly searchSubject = new BehaviorSubject<string>('');
  private readonly columnFiltersSubject = new BehaviorSubject<{ nombre: string }>({ nombre: '' });

  // Data source para la tabla
  dataSource = new MatTableDataSource<TipoContrato>([]);
  displayedColumns: string[] = ['tipoContratoId', 'nombre', 'actions'];

  // Observable combinado para mostrar tipos de contrato aplicando búsqueda y filtros por columna
  displayTiposContrato$ = combineLatest([
    this.tiposContrato$,
    this.searchSubject,
    this.columnFiltersSubject
  ]).pipe(
    map(([tiposContrato, globalSearch, colFilters]) => {
      let result = tiposContrato;

      // Búsqueda general
      const globalQuery = (globalSearch || '').toLowerCase().trim();
      if (globalQuery) {
        result = result.filter(tc => tc.nombre.toLowerCase().includes(globalQuery));
      }

      // Filtros por columna
      const { nombre } = colFilters;
      if (nombre && nombre.trim()) {
        const q = nombre.toLowerCase().trim();
        result = result.filter(tc => tc.nombre.toLowerCase().includes(q));
      }

      return result;
    })
  );

  // Propiedades para paginación
  pageSize = 10;
  pageSizeOptions = [5, 10, 25, 50];
  pageIndex = 0;
  totalItems = 0;

  // Propiedades para búsqueda
  searchQuery = '';
  showSearchPanel = false;

  // Formulario unificado
  tipoContratoForm: FormGroup;

  // Referencias a templates
  @ViewChild('createTipoContratoModal') createTipoContratoModal!: TemplateRef<unknown>;
  @ViewChild('paginator') set paginator(p: MatPaginator) {
    if (p) {
      this._paginator = p;
      this.dataSource.paginator = p;
    }
  }
  private _paginator!: MatPaginator;

  // Estado del componente
  selectedTipoContrato: TipoContrato | null = null;
  isEditing = false;
  editingTipoContrato: TipoContrato | null = null;

  // Estados para rastrear operaciones
  wasCreating = false;
  wasUpdating = false;
  wasDeleting = false;

  // TrackBy functions
  trackByTipoContratoId = (index: number, item: TipoContrato): number => {
    return item?.tipoContratoId || index;
  };

  constructor() {
    this.tipoContratoForm = this.formBuilder.group({
      nombre: ['', [Validators.required, Validators.maxLength(100)]]
    });
  }

  ngOnInit(): void {
    this.loadTiposContrato();
    this.setupSubscriptions();
  }

  ngAfterViewInit(): void {
    // Conectar el paginador al data source inmediatamente
    this.dataSource.paginator = this._paginator;

    // Asegurar que el paginador esté configurado correctamente
    if (this._paginator) {
      this._paginator.pageSize = this.pageSize;
      this._paginator.pageSizeOptions = this.pageSizeOptions;
    }

    // Inicializar subjects de filtros
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ nombre: '' });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSubscriptions(): void {
    // Suscribirse a los cambios en displayTiposContrato$ para actualizar el data source
    this.displayTiposContrato$.pipe(takeUntil(this.destroy$)).subscribe(tiposContrato => {
      // Actualizar el data source existente
      this.dataSource.data = tiposContrato;

      // Asegurar que el paginador esté conectado
      if (this._paginator && !this.dataSource.paginator) {
        this.dataSource.paginator = this._paginator;
      }
    });

    // Suscripción a errores
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error);
        this.store.dispatch(TipoContratoActions.clearTipoContratoError());
      }
    });

    // Suscripción a errores (similar a actuación contractual)
    this.subscribeToError();

    // Suscripción a creación exitosa
    this.creating$.pipe(takeUntil(this.destroy$)).subscribe(creating => {
      if (!creating && this.wasCreating) {
        this.notificationService.showSuccessModal('Tipo de contrato creado exitosamente');
      }
      this.wasCreating = creating;
    });

    // Suscripción a actualización exitosa
    this.updating$.pipe(takeUntil(this.destroy$)).subscribe(updating => {
      if (!updating && this.wasUpdating) {
        this.notificationService.showSuccessModal('Tipo de contrato actualizado exitosamente');
      }
      this.wasUpdating = updating;
    });

    // Suscripción a eliminación exitosa
    this.deleting$.pipe(takeUntil(this.destroy$)).subscribe(deleting => {
      if (!deleting && this.wasDeleting) {
        this.notificationService.showSuccessModal('Tipo de contrato eliminado exitosamente');
        this.selectedTipoContrato = null;
      }
      this.wasDeleting = deleting;
    });
  }

  private loadTiposContrato(): void {
    this.store.dispatch(TipoContratoActions.loadTiposContrato({ force: false }));
  }

  // Métodos de filtrado
  toggleSearch(): void {
    this.showSearchPanel = !this.showSearchPanel;
  }

  // Métodos de modal
  openCreateModal(tipoContrato?: TipoContrato): void {
    this.isEditing = !!tipoContrato;
    this.editingTipoContrato = tipoContrato || null;

    if (this.isEditing && tipoContrato) {
      this.tipoContratoForm.patchValue({
        nombre: tipoContrato.nombre
      });
    } else {
      this.tipoContratoForm.reset();
    }

    this.dialog.open(this.createTipoContratoModal, {
      width: '500px',
      disableClose: false
    });
  }

  closeModal(): void {
    this.dialog.closeAll();
    this.resetForm();
  }

  private resetForm(): void {
    this.tipoContratoForm.reset();
    this.isEditing = false;
    this.editingTipoContrato = null;
  }

  // Métodos de CRUD
  async saveTipoContrato(): Promise<void> {
    if (this.tipoContratoForm.valid) {
      const formData = this.tipoContratoForm.value;
      const nombre = formData.nombre || '';

      if (this.isEditing && this.editingTipoContrato) {
        // Para editar, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea actualizar este tipo de contrato?',
          'Confirmar Actualización'
        );

        if (confirmed) {
          // Check if name already exists (excluding current item)
          this.tiposContrato$.pipe(take(1)).subscribe(tiposContrato => {
            const existingTipo = tiposContrato.find(
              tc =>
                tc.nombre.toLowerCase() === nombre.toLowerCase() &&
                tc.tipoContratoId !== this.editingTipoContrato!.tipoContratoId
            );

            if (existingTipo) {
              this.notificationService.showErrorModal(
                'Error de validación',
                'Ya existe un tipo de contrato con este nombre'
              );
              return;
            }

            const updateRequest = {
              Nombre: nombre
            };

            this.store.dispatch(
              TipoContratoActions.updateTipoContrato({
                id: this.editingTipoContrato.tipoContratoId,
                tipoContrato: updateRequest
              })
            );

            // Cerrar modal después de actualizar
            this.closeModal();
          });
        }
      } else {
        // Para crear, mostrar confirmación primero
        const confirmed = await this.notificationService.showConfirmation(
          '¿Está seguro de que desea crear este tipo de contrato?',
          'Confirmar Creación'
        );

        if (confirmed) {
          // Check if name already exists
          this.tiposContrato$.pipe(take(1)).subscribe(tiposContrato => {
            const existingTipo = tiposContrato.find(
              tc => tc.nombre.toLowerCase() === nombre.toLowerCase()
            );

            if (existingTipo) {
              this.notificationService.showErrorModal(
                'Error de validación',
                'Ya existe un tipo de contrato con este nombre'
              );
              return;
            }

            const createRequest = {
              Nombre: nombre
            };

            this.store.dispatch(
              TipoContratoActions.createTipoContrato({ tipoContrato: createRequest })
            );

            // Cerrar modal después de crear
            this.closeModal();
          });
        }
      }
    } else {
      this.notificationService.showErrorModal(
        'Error de validación',
        'Por favor complete todos los campos requeridos'
      );
    }
  }

  async deleteTipoContrato(): Promise<void> {
    if (this.selectedTipoContrato) {
      const confirmed = await this.notificationService.showConfirmation(
        '¿Está seguro de que desea eliminar este tipo de contrato?',
        'Confirmar Eliminación'
      );

      if (confirmed) {
        this.store.dispatch(
          TipoContratoActions.deleteTipoContrato({
            id: this.selectedTipoContrato.tipoContratoId
          })
        );
        this.selectedTipoContrato = null; // Clear selection after delete
      }
    } else {
      this.notificationService.showErrorModal(
        'Error de selección',
        'No hay tipo de contrato seleccionado para eliminar'
      );
    }
  }

  selectTipoContrato(tipoContrato: TipoContrato): void {
    this.selectedTipoContrato = tipoContrato;
  }

  // Métodos para filtros y búsqueda
  searchTiposContrato(query: string): void {
    this.searchQuery = query;
    this.searchSubject.next(this.searchQuery);
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  onColumnFilterChange(field: keyof typeof this.columnFilters, value: string): void {
    this.columnFilters[field] = value;
    this.columnFiltersSubject.next({ ...this.columnFilters });
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  applyFilters(): void {
    this.searchSubject.next(this.searchQuery);
    this.columnFiltersSubject.next({ ...this.columnFilters });
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.columnFilters = { nombre: '' };
    this.searchSubject.next('');
    this.columnFiltersSubject.next({ nombre: '' });
    if (this._paginator) {
      this._paginator.firstPage();
    }
  }

  resetFilters(): void {
    this.clearFilters();
  }

  onPageChange(): void {
    // El paginador se maneja automáticamente por el MatTableDataSource
  }

  private subscribeToError(): void {
    this.error$.pipe(takeUntil(this.destroy$)).subscribe(error => {
      if (error) {
        this.errorHandler.handleError(error);
      }
    });
  }

  // Eliminated success subscriptions and forced reloads; signals keep the view in sync.

  // Métodos de utilidad
  hasError(controlName: string): boolean {
    const control = this.tipoContratoForm.get(controlName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  getErrorMessage(fieldName: string): string {
    const field = this.tipoContratoForm.get(fieldName);
    if (field?.hasError('required')) {
      return `${fieldName} es requerido`;
    }
    if (field?.hasError('maxlength')) {
      return `${fieldName} no puede exceder los 100 caracteres`;
    }
    return '';
  }
}
